/**
 * useWebVitals — React hook that subscribes to Core Web Vitals events and
 * forwards each measurement to a user-supplied callback.
 *
 * Metrics reported (web-vitals v5+):
 *   - CLS  Cumulative Layout Shift
 *   - INP  Interaction to Next Paint
 *   - LCP  Largest Contentful Paint
 *   - FCP  First Contentful Paint
 *   - TTFB Time to First Byte
 *
 * NOTE: FID (First Input Delay) was removed from web-vitals v4+ and is
 * superseded by INP in the Core Web Vitals set.
 *
 * Usage:
 *   useWebVitals((metric) => reportWebVitals(metric));
 *
 * The hook is safe to call in SSR / pre-render environments — it no-ops
 * when `window` is absent. It subscribes once on mount and never
 * re-subscribes on re-renders (the callback ref pattern ensures the
 * latest callback is always used without triggering re-subscription).
 *
 * Spec: docs/CHAT-UI-IMPROVEMENT-DESIGN.md §TrackA — Core Web Vitals
 */

import { useEffect, useRef } from 'react';
import type { Metric } from 'web-vitals';

export type WebVitalsCallback = (metric: Metric) => void;

/**
 * Subscribe to all Core Web Vitals metrics.
 *
 * @param onMetric   Called once per metric, as soon as data is available.
 *                   CLS/INP may fire multiple times (updated on page hide).
 * @param reportAllChanges   When true, every CLS / INP update fires the
 *                           callback (not just the final value). Defaults
 *                           to false — only the final / best-available
 *                           value is reported per page lifecycle.
 */
export function useWebVitals(
  onMetric: WebVitalsCallback,
  reportAllChanges = false,
): void {
  // Keep a stable ref to the latest callback so the effect closure never
  // captures a stale copy, and we never need to re-subscribe.
  const callbackRef = useRef<WebVitalsCallback>(onMetric);
  useEffect(() => {
    callbackRef.current = onMetric;
  });

  useEffect(() => {
    // Guard: web-vitals only works in a browser context.
    if (typeof window === 'undefined') return;

    let cancelled = false;

    const report = (metric: Metric): void => {
      if (!cancelled) callbackRef.current(metric);
    };

    // Dynamic import keeps this out of the initial JS bundle chunk.
    // The web-vitals library is ~2 KB gzipped and loads fast, but
    // separating it avoids any risk of blocking LCP itself.
    import('web-vitals').then(({ onCLS, onINP, onLCP, onFCP, onTTFB }) => {
      if (cancelled) return;
      const opts = { reportAllChanges };
      onCLS(report, opts);
      onINP(report, opts);
      onLCP(report, opts);
      onFCP(report, opts);
      onTTFB(report, opts);
    }).catch(() => {
      // Silently ignore import failures (unlikely but possible in highly
      // restricted CSP environments).
    });

    return () => {
      cancelled = true;
    };
  // Effect runs once; reportAllChanges is intentionally excluded from
  // deps — changing it mid-session would require re-subscription, which
  // is not a supported use-case. The value at mount time is what matters.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
